import {ProductSearchFieldsEnum} from "./productSearchFieldsEnum.js";
import {ProductResultsEnum} from "./productResultsEnum.js";
import {ConfigService} from '@nuskin/ns-util';
import * as jpConverter from 'jp-conversion';

/**
 *
 * SolrQueryUrlService
 *
 * @namespace SolrQueryUrlService
 */
export const SolrQueryUrlService = function() {
    // Solr product keys - !!! Warning, don't change the string values. They map directly to the pmd productDetail node. !!!
    let PATH_EXACT = "path_exact";
    let SKU = "sku";
    let ISEXCLUSIVE = "isExclusive";
    let MARKETS = "markets";
    let LANGUAGES = "languages";
    let TITLE = "name";
    let DESCRIPTION = "shortDescription";
    let LONG_DESCRIPTION = "longDescription";
    let PRODUCTS = "products";
    let KEY_INGREDIENTS = "ingredients";
    let CATEGORIES = "categories";
    let SEARCH_TERMS = "searchTerms";
    let BENEFITS = "benefits";
    let USAGE = "usage";
    // let PURCHASABLE = "webEnabled";
    let IMAGE_URL = "fullImage";
    // let PV = "pv";
    // let RETAIL_PRICE = "rtl";
    let BASE_SKU = "baseSku";
    let VARIANTS = "variants";
    let ROOT = "_root_";
    let SCORE = "score";
    // let CHILD_DOCS = "_childDocuments_";
    // let VERSION = "_version_";
    // let EXCLUDE_FROM_SEARCH = "AND (excludeFromSearch:false OR (*:* NOT excludeFromSearch:*))";

    // Solr query parameters
    let PRODUCT_INDEX_PREFIX = "product/";
    let OR = " OR ";
    let ESCAPEDOR = "%20OR%20";
    let SKU_WILDCARD = "????????";
    let PRODUCT_INDEX_QUERY_PARAM = PATH_EXACT + ":" + PRODUCT_INDEX_PREFIX;
    let CHILD_FILTER = " childFilter=";
    let PARENT_FILTER = "child parentFilter=";
    let DEFAULT_START_VALUE = 0;
    let DEFAULT_ROW_VALUE = 50;
    let MAX_ROW_VALUE = 500;
    let RETURN_JSON = "&wt=json";

    // Solr url path stuff
    let SOLR_SELECT_URI = "products/select?wt=json"; //"/solr/oak/select?fl=";
    // let JCR_CONTENT = "jcr:content";
    // let PMD_PATH_PREFIX = "/content/pmd/";
    // let PIM_PATH_PREFIX = "/etc/commerce/products/nuskin/";
    // let PMD_PRODUCT_CONTAINER_SUFFIX = "/jcr:content/productContainer";
    // let PMD_PRODUCT_DETAIL_SUFFIX = "/jcr:content/productContainer/productDetail";
    // let SOLR_UPDATE_SUFFIX_URI  = "products/update/json?wt=json&commit=true";

    // Misc
    let PRODUCT_PREFIX_AND_SKU_LENGTH = PRODUCT_INDEX_PREFIX.length + SKU_WILDCARD.length;

    // Mulesoft solr endpoint
    let mulesoftSolrEndpoint;

    //Boosting factors
    let productTitleBoostingFactor = "^8.5";
    let productSkuBoostingFactor = "^2";
    let productDescriptionBoostingFactor = "^4.5";
    let productLongDescriptionBoostingFactor = "^4.5";
    let productProductsBoostingFactor = "^1";
    let productIngredientsBoostingFactor = "^6.5";
    let productCategoriesBoostingFactor = "^1";
    let productSearchTermsBoostingFactor = "^10.5";
    let productUsageBoostingFactor = "^1";
    let productBenefitsBoostingFactor = "^3";

    //sort by clause
    let sortByProductTitle = "&sort=score%20desc,productSuggestion%20asc";

    let disableLowerCaseOp = "&lowercaseOperators=false";

    //---------------------------------------------------------------------------
    //
    //                           Public Methods
    //
    //---------------------------------------------------------------------------
    let publicMethods = {
        /**
         * Search the language document for search term matches
         *
         * @memberof SolrQueryUrlService
         * @example
         * SolrQueryUrlService.getProductRootsForSearchTerms(productResultType, searchTerm, productSearchFields, language, market, rows, start)
         * @param productResultType
         * @param searchTerm
         * @param productSearchFields
         * @param language
         * @param market
         * @param rows
         * @param start
         * @returns {string}
         */
        getProductRootsForSearchTerms: function (productResultType, searchTerm, productSearchFields, language, market, rows, start){
            let queryData = initializeQueryData();
            queryData.postData.params.q = PRODUCT_INDEX_QUERY_PARAM + SKU_WILDCARD + "/" + language.toLowerCase();
            queryData.postData.params.fl = ROOT;

            // See if we need to include base products
            if (productResultType === ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS){
                // We need to exclude the variant products. (Variant products contain baseSku).
                queryData.postData.filter.push("-" + BASE_SKU + ":*");
            }

            // See if we need to include variant products
            if (productResultType === ProductResultsEnum.PRODUCTS_AND_VARIANT_PRODUCTS){
                // We need to exclude the base products. (Base products have variants).
                queryData.postData.filter.push("-" + VARIANTS + ":*")
            }

            queryData.postData.filter.push(MARKETS + ":" + market.toUpperCase());
            queryData.postData.filter.push(getSearchTermQuery(searchTerm, productSearchFields));
            return addResultFilter(rows, start, queryData);
        },

        /**
         * Search the  documents for search term matches and give more weight to ProductTitleField.
         * @memberof SolrQueryUrlService
         * @example
         * SolrQueryUrlService.getBasicProductDataForSearchTermsWithFieldLevelBoosting(productResultType, searchTerm, productSearchFields,
         * language, market, rows, start)
         * @param productResultType
         * @param searchTerm
         * @param productSearchFields
         * @param language
         * @param market
         * @param rows
         * @param start
         * @returns {string}
         */
        getBasicProductDataForSearchTermsWithFieldLevelBoosting:
            function (productResultType, searchTerm, productSearchFields, language, market, rows, start) {
                let resulteDisMaxQueryStr = "fq="+PRODUCT_INDEX_QUERY_PARAM + SKU_WILDCARD + "/" + language.toLowerCase();

                if (productResultType === ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS){
                    // We need to exclude the variant products. (Variant products contain baseSku).
                    resulteDisMaxQueryStr += "&fq=-" + BASE_SKU + ":*";
                }
                if (productResultType === ProductResultsEnum.PRODUCTS_AND_VARIANT_PRODUCTS){
                    // We need to exclude the base products. (Base products have variants).
                    resulteDisMaxQueryStr += "&fq=-" + VARIANTS + ":*";
                }
                if (productResultType === ProductResultsEnum.BASE_PRODUCTS){
                    // We need to include only the base products. (Base products have variants).
                    resulteDisMaxQueryStr += "&fq=" + VARIANTS + ":*";
                }
                if (productResultType === ProductResultsEnum.VARIANT_PRODUCTS){
                    // We need to include only the base products. (Base products have variants).
                    resulteDisMaxQueryStr += "&fq=" + BASE_SKU + ":*";
                }


                return getMulesoftSolrEndpoint() +
                    SOLR_SELECT_URI + "&fl=" + ROOT + "," + SCORE + "," + TITLE + "," + IMAGE_URL + "," + SKU + "," + ISEXCLUSIVE +
                    getSearchTermQueryWithFieldLevelBoosting(searchTerm, productSearchFields) +
                    "&fq=" +
                    MARKETS + ":" + market.toUpperCase() +
                    getFilterQueryforEdisMax(resulteDisMaxQueryStr, rows, start)+
                    sortByProductTitle+disableLowerCaseOp;
            },

        /**
         * Search the  documents for search term matches and give more weight to ProductTitleField.
         * @memberof SolrQueryUrlService
         * @example
         * SolrQueryUrlService.getProductRootsForSearchTermsWithFieldLevelBoosting(productResultType,
         * searchTerm, productSearchFields, language, market, rows, start)
         * @param productResultType
         * @param searchTerm
         * @param productSearchFields
         * @param language
         * @param market
         * @param rows
         * @param start
         * @returns {string}
         */
        getProductRootsForSearchTermsWithFieldLevelBoosting:
            function (productResultType, searchTerm, productSearchFields, language, market, rows, start) {
                let resulteDisMaxQueryStr = "fq="+PRODUCT_INDEX_QUERY_PARAM + SKU_WILDCARD + "/" + language.toLowerCase();

                // See if we need to include base products
                if (productResultType === ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS){
                    // We need to exclude the variant products. (Variant products contain baseSku).
                    resulteDisMaxQueryStr += "&fq=-" + BASE_SKU + ":*";
                }

                // See if we need to include variant products
                if (productResultType === ProductResultsEnum.PRODUCTS_AND_VARIANT_PRODUCTS){
                    // We need to exclude the base products. (Base products have variants).
                    resulteDisMaxQueryStr += "&fq=-" + VARIANTS + ":*";
                }


                return getMulesoftSolrEndpoint() +
                    SOLR_SELECT_URI + "&fl="+ROOT +","+SCORE+
                    getSearchTermQueryWithFieldLevelBoosting(searchTerm, productSearchFields) +
                    "&fq=" +
                    MARKETS + ":" + market.toUpperCase() +
                    getFilterQueryforEdisMax(resulteDisMaxQueryStr, rows, start)+
                    sortByProductTitle+disableLowerCaseOp;
            },

        /**
         * Get the products specified by the root product skus.
         * @memberof SolrQueryUrlService
         * @example
         * SolrQueryUrlService.getProductsByRoots(matchingRootProducts, language, market, rows, start)
         * @param matchingRootProducts
         * @param language
         * @param market
         * @param rows
         * @param start
         * @param orderTypes
         * @returns {string}
         */
        getProductsByRoots: function (matchingRootProducts, language, market, rows, start, orderTypes) {
            let queryData = initializeQueryData();

            if (matchingRootProducts.length > 0) {
                // Get the products for the matching root nodes
                let rootNodesQuery = "";
                let rootPath;
                for (let i = 0; i < matchingRootProducts.length; i++) {
                    rootPath = matchingRootProducts[i][ROOT];

                    if (rootPath[0].length === PRODUCT_PREFIX_AND_SKU_LENGTH) {
                        if (rootNodesQuery.length > 0){
                            rootNodesQuery += OR;
                        }
                        rootNodesQuery += PATH_EXACT + ":" + rootPath + "^" + (10000/(i+1)).toString();
                    }
                }

                queryData.postData.params.fl = "*," + getChildrenFilter(SKU_WILDCARD, language.toLowerCase()) + "," +
                    getChildrenFilter(SKU_WILDCARD, market.toUpperCase());

                if (orderTypes && orderTypes.constructor === Array && orderTypes.length > 0) {
                    let orderTypeFilter = "{!join from=sku to=sku}orderType:" + orderTypes[0];
                    for (let i = 1; i < orderTypes.length; i++) {
                        orderTypeFilter += " OR orderType:" + orderTypes[i];
                    }
                    //Base SKUs do not have order types, so we will include any base SKUs in our query as well
                    orderTypeFilter += " OR variants:*"
                    queryData.postData.filter.push(orderTypeFilter);
                }

                queryData.postData.filter.push(MARKETS + ":" + market.toUpperCase());
                queryData.postData.filter.push(LANGUAGES + ":" + language.toLowerCase());
                queryData.postData.params.q = rootNodesQuery;

                queryData = addResultFilter(rows, start, queryData);
            }

            return queryData;
        },

        /**
         * Get all products for a particular market and language based on the desired productResultType.
         * @memberof SolrQueryUrlService
         * @example
         * SolrQueryUrlService.getProducts(productResultType, language, market, rows, start)
         * @param productResultType
         * @param language
         * @param market
         * @param rows
         * @param start
         * @returns {string}
         */
        getProducts: function(productResultType, language, market, rows, start){
            let queryData = initializeQueryData();

            switch (productResultType){
                case ProductResultsEnum.BASE_PRODUCTS:
                    queryData.postData.filter.push(BASE_SKU + ":*");
                    break;
                case ProductResultsEnum.VARIANT_PRODUCTS:
                    queryData.postData.filter.push(VARIANTS + ":*");
                    break;
            }

            queryData.postData.filter.push(LANGUAGES + ":" + language.toLowerCase());
            queryData.postData.filter.push(MARKETS + ":" + market.toUpperCase());
            queryData.postData.params.fl = "*," + getChildrenFilter(SKU_WILDCARD, '*');
            return addResultFilter(rows, start, queryData);
        },

        getProductRootsForSKUArray: function (skuArray/*, productSearchFields, language, market*/){
            let productRoots = [];
            for(let i = 0; i < skuArray.length; i++)
            {
                let productRoot = {_root_:[PRODUCT_INDEX_PREFIX + skuArray[i].toString()]};
                productRoots.push(productRoot);
            }
            return productRoots;
        },
        /**
         * Get the products specified by the root product skus.
         * @memberof SolrQueryUrlService
         * @example
         * SolrQueryUrlService.getProductsByRootsWithTermBoosting(matchingRootProducts, language, market, rows, start)
         * @param matchingRootProducts
         * @param language
         * @param market
         * @param rows
         * @param start
         * @returns {string}
         */
        getProductsByRootsWithTermBoosting: function (matchingRootProducts, language, market, rows, start) {
            let solrUrl = "";

            if (matchingRootProducts.length > 0) {
                // Get the products for the matching root nodes
                let rootNodesQuery = "";
                let rootPath;
                for (let i = 0; i < matchingRootProducts.length; i++) {
                    rootPath = matchingRootProducts[i][ROOT];

                    if (rootPath[0].length === PRODUCT_PREFIX_AND_SKU_LENGTH) {
                        if (rootNodesQuery.length > 0){
                            rootNodesQuery += ESCAPEDOR;
                        }
                        let weight = (10000/(i+1));
                        rootNodesQuery += PATH_EXACT + ":" + rootPath+"^"+weight;
                    }
                }

                // Return the complete records for the given matches
                // Example solrUrl: http://dev-gateway-mulesoft-21:8080/mec/solr/oak/select?fl=*,
                // [child%20parentFilter=path_exact:product/????????%20childFilter=path_exact:product/????????/en],
                // [child%20parentFilter=path_exact:product/????????%20childFilter=path_exact:product/????????/US]&
                // q=path_exact:product/01003611&rows=5&wt=json
                solrUrl =
                    getMulesoftSolrEndpoint() +
                    SOLR_SELECT_URI + "&fl=*," +
                    getChildrenFilter(SKU_WILDCARD, language.toLowerCase()) + "," +
                    getChildrenFilter(SKU_WILDCARD, market.toUpperCase()) +
                    "&fq=" +
                    MARKETS + ":" + market.toUpperCase() + "%20&&%20" +
                    LANGUAGES + ":" + language.toLowerCase() +
                    getResultFilter(rootNodesQuery, rows, start) +
                    sortByProductTitle;
            }

            return solrUrl;
        }
    };

    //---------------------------------------------------------------------------
    //
    //                            Private Methods
    //
    //---------------------------------------------------------------------------
    function getSearchTermQuery(searchTerm, productSearchFields){
        let queryString = "";

        // We want to match any place the searchTerm is contained.
        let escapedSearchTerm = doctorSearchTerm(searchTerm);
        if (productSearchFields.length > 0) {
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.SKU) > -1) {
                queryString += SKU + ":" + escapedSearchTerm + OR;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.TITLE)> -1) {
                queryString += TITLE + ":" + escapedSearchTerm + OR;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.DESCRIPTION)> -1) {
                queryString += DESCRIPTION + ":" + escapedSearchTerm + OR;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.LONG_DESCRIPTION)> -1) {
                queryString += LONG_DESCRIPTION + ":" + escapedSearchTerm + OR;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.PRODUCTS)> -1) {
                queryString += PRODUCTS + ":" + escapedSearchTerm + OR;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.KEY_INGREDIENTS)> -1) {
                queryString += KEY_INGREDIENTS + ":" + escapedSearchTerm + OR;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.CATEGORIES)> -1) {
                queryString += CATEGORIES + ":" + escapedSearchTerm + OR;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.SEARCH_TERMS)> -1) {
                queryString += SEARCH_TERMS + ":" + escapedSearchTerm + OR;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.USAGE)> -1) {
                queryString += USAGE + ":" + escapedSearchTerm + OR;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.BENEFITS)> -1) {
                queryString += BENEFITS + ":" + escapedSearchTerm + OR;
            }

            // Remove the last "OR".
            let lastIndexOfOr = queryString.lastIndexOf(OR);

            if (lastIndexOfOr != -1) {
                queryString = queryString.substring(0, lastIndexOfOr);
            }
        }

        return queryString;
    }

    function getSearchTermQueryWithFieldLevelBoosting(searchTerm, productSearchFields){
        let queryString = "&defType=edismax";

        // We want to match any place the searchTerm is contained.
        let escapedSearchTerm = doctorSearchTerm(searchTerm);
        //escapedSearchTerm = applyTermLevelBoosting(escapedSearchTerm);
        escapedSearchTerm = escapedSearchTerm.toLowerCase();
        //This next line is needed because OR in solr is case sensitive, so we need to fix that from the previous line
        escapedSearchTerm = escapedSearchTerm.replace('*" or *', '*" OR *');
        if (productSearchFields.length > 0) {
            queryString += "&q="+escapedSearchTerm+"&qf=";

            if (productSearchFields.indexOf(ProductSearchFieldsEnum.SKU) > -1) {
                queryString += SKU +productSkuBoostingFactor+ " ";
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.TITLE)> -1) {
                queryString += TITLE +productTitleBoostingFactor+ " " ;
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.DESCRIPTION)> -1) {
                queryString += DESCRIPTION +productDescriptionBoostingFactor+ " ";
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.LONG_DESCRIPTION)> -1) {
                queryString += LONG_DESCRIPTION +productLongDescriptionBoostingFactor+ " ";
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.PRODUCTS)> -1) {
                queryString += PRODUCTS +productProductsBoostingFactor+ " ";
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.KEY_INGREDIENTS)> -1) {
                queryString += KEY_INGREDIENTS +productIngredientsBoostingFactor+ " ";
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.CATEGORIES)> -1) {
                queryString += CATEGORIES +productCategoriesBoostingFactor+ " ";
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.SEARCH_TERMS)> -1) {
                queryString += SEARCH_TERMS +productSearchTermsBoostingFactor+ " ";
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.USAGE)> -1) {
                queryString += USAGE +productUsageBoostingFactor+ " ";
            }
            if (productSearchFields.indexOf(ProductSearchFieldsEnum.BENEFITS)> -1) {
                queryString += BENEFITS +productBenefitsBoostingFactor+ " ";
            }

        }

        return queryString;
    }

    function getFilterQueryforEdisMax(queryParam, rows, start){
        let filter = "";

        if (!rows){
            rows = DEFAULT_ROW_VALUE;
        }
        else if (rows > MAX_ROW_VALUE){
            rows = MAX_ROW_VALUE;
        }
        if (!start){
            start = DEFAULT_START_VALUE;
        }

        if (queryParam && queryParam.length > 0){
            filter += "&" + queryParam;
        }

        filter += "&rows=" + rows + "&start=" + start + RETURN_JSON;

        return filter;
    }

    function getChildrenFilter(sku, childIndexSuffix){
        return "[" + PARENT_FILTER + PRODUCT_INDEX_QUERY_PARAM + sku + CHILD_FILTER + PRODUCT_INDEX_QUERY_PARAM + sku + "/" + childIndexSuffix + "]";
    }

    function getResultFilter(queryParam, rows, start){
        let filter = "";

        if (!rows){
            rows = DEFAULT_ROW_VALUE;
        }
        else if (rows > MAX_ROW_VALUE){
            rows = MAX_ROW_VALUE;
        }
        if (!start){
            start = DEFAULT_START_VALUE;
        }

        if (queryParam && queryParam.length > 0){
            filter += "&q=" + queryParam;
        }

        filter += "&rows=" + rows + "&start=" + start + RETURN_JSON;

        return filter;
    }

    function addResultFilter(rows, start, queryData){
        if (!rows){
            rows = DEFAULT_ROW_VALUE;
        }
        else if (rows > MAX_ROW_VALUE){
            rows = MAX_ROW_VALUE;
        }
        if (!start){
            start = DEFAULT_START_VALUE;
        }
        queryData.postData.params.rows = rows;
        queryData.postData.params.start = start;

        return queryData;
    }

    function doctorSearchTerm(searchTerm){
        const REGEX_JA_ZH = /[\u3000-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f]/;
        const hasMatch = searchTerm.match(REGEX_JA_ZH);
        if(hasMatch) {
            searchTerm = convertJapanese(searchTerm);
        }
        return '"*' + searchTerm + '*" OR *' + searchTerm + '*';
    }

    function getMulesoftSolrEndpoint(){
        if (!mulesoftSolrEndpoint){
            mulesoftSolrEndpoint = ConfigService.getMarketConfig().solrBaseUrl;
        }
        return mulesoftSolrEndpoint;
    }

    /*
    function applyTermLevelBoosting(escapedSearchTerm){
        let escapedSearchTermArray = escapedSearchTerm.split(' ');
        let firstTerm = "";
        let boostedFirstTerm = "";
        if(Array.isArray(escapedSearchTermArray)&& escapedSearchTermArray.length>1){
            firstTerm = escapedSearchTermArray[0];
            boostedFirstTerm = firstTerm + "^20";
        }
        escapedSearchTerm = escapedSearchTerm.replace(firstTerm,boostedFirstTerm);
        return escapedSearchTerm;
    }
    */

    function initializeQueryData() {
        let queryData = {};
        queryData.url = getMulesoftSolrEndpoint() + SOLR_SELECT_URI;
        queryData.postData = {};
        queryData.postData.params = {};
        queryData.postData.filter = [];
        return queryData;
    }

    function convertJapanese(term) {
        let res = jpConverter.convert(term);
        if (res) {
            if (res.katakana) {
                return res.katakana;
            }
            else if (res.hiragana) {
                return res.hiragana;
            }
            else if (res.kanji) {
                return res.kanji;
            }
            else {
                return res;
            }
        }
        return term;
    }

    return publicMethods;
}();
